import React, { useState, useEffect } from 'react';
import { Card, Spin, Table, Input, Button, DatePicker, Radio, Select, Drawer, Space } from 'antd';
import {
  DollarOutlined,
  ReloadOutlined,
  SearchOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import ReportService from '../../../services/ReportService';
import ReportCompanySelect from '../../../components/ReportCompanySelect';
import ReportBranchSelect from '../../../components/ReportBranchSelect';
import { getUpdatedMobileDateRange } from '../../../helpers/mobileDateRange';
import { useReportScope } from '../../../hooks/useReportScope';

const { RangePicker } = DatePicker;
const { Option } = Select;

const PaymentReports = () => {
  const [loading, setLoading] = useState(true);
  const [dateFilterType, setDateFilterType] = useState('quick');
  const [dateFilter, setDateFilter] = useState('today');
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [dateRange, setDateRange] = useState([dayjs().subtract(7, 'day'), dayjs()]);
  const [searchText, setSearchText] = useState('');
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [draftDateFilterType, setDraftDateFilterType] = useState('quick');
  const [draftDateFilter, setDraftDateFilter] = useState('today');
  const [draftSelectedDate, setDraftSelectedDate] = useState(dayjs());
  const [draftDateRange, setDraftDateRange] = useState([dayjs().subtract(7, 'day'), dayjs()]);
  const [draftSelectedBranch, setDraftSelectedBranch] = useState(null);
  const [draftAdminCompanyCode, setDraftAdminCompanyCode] = useState(null);

  const [toplamCiro, setToplamCiro] = useState([]);
  const [nakit, setNakit] = useState([]);
  const [krediKarti, setKrediKarti] = useState([]);
  const [yemekKarti, setYemekKarti] = useState([]);

  const [toplamCiroLoading, setToplamCiroLoading] = useState(false);
  const [nakitLoading, setNakitLoading] = useState(false);
  const [krediKartiLoading, setKrediKartiLoading] = useState(false);
  const [yemekKartiLoading, setYemekKartiLoading] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const {
    token,
    allBranches,
    branchesLoading,
    companies,
    adminCompanyCode,
    setAdminCompanyCode,
    effectiveCompanyCode,
    needsCompanyPicker,
    getCompanyCodeForRequest,
  } = useReportScope();

  const getDateParams = (filterOverride = null, filterTypeOverride = null, dateRangeOverride = null, selectedDateOverride = null) => {
    const currentFilter = filterOverride !== null ? filterOverride : dateFilter;
    const currentFilterType = filterTypeOverride !== null ? filterTypeOverride : dateFilterType;
    const currentDateRange = dateRangeOverride !== null ? dateRangeOverride : dateRange;
    const currentSelectedDate = selectedDateOverride !== null ? selectedDateOverride : selectedDate;

    if (currentFilterType === 'range' && currentDateRange && currentDateRange[0] && currentDateRange[1]) {
      return {
        StartDate: currentDateRange[0].startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        EndDate: currentDateRange[1].add(1, 'day').startOf('day').format('YYYY-MM-DD HH:mm:ss')
      };
    }
    if (currentFilterType === 'quick' && currentFilter === 'custom') {
      return {
        StartDate: currentSelectedDate.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        EndDate: currentSelectedDate.add(1, 'day').startOf('day').format('YYYY-MM-DD HH:mm:ss')
      };
    }
    return {
      DateFilter: currentFilter
    };
  };

  const fetchToplamCiro = async (filterOverride = null, filterTypeOverride = null, dateRangeOverride = null, selectedDateOverride = null, branchCodeOverride = null, adminCompanyOverride = undefined) => {
    const resolvedBranchCode = branchCodeOverride !== null && branchCodeOverride !== undefined ? branchCodeOverride : (selectedBranch || null);
    const companyCode = getCompanyCodeForRequest(resolvedBranchCode, adminCompanyOverride);
    if (!companyCode) {
      setToplamCiro([]);
      return;
    }
    try {
      setToplamCiroLoading(true);
      const dateParams = getDateParams(filterOverride, filterTypeOverride, dateRangeOverride, selectedDateOverride);
      const response = await ReportService.dashboard.getBranchRevenueReport({
        token,
        CompanyCode: companyCode,
        BranchCode: resolvedBranchCode || undefined,
        ...dateParams
      });

      if (response.status === 200) {
        setToplamCiro(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching toplam ciro:', error);
    } finally {
      setToplamCiroLoading(false);
    }
  };

  const fetchNakit = async (filterOverride = null, filterTypeOverride = null, dateRangeOverride = null, selectedDateOverride = null, branchCodeOverride = null, adminCompanyOverride = undefined) => {
    const resolvedBranchCode = branchCodeOverride !== null && branchCodeOverride !== undefined ? branchCodeOverride : (selectedBranch || null);
    const companyCode = getCompanyCodeForRequest(resolvedBranchCode, adminCompanyOverride);
    if (!companyCode) {
      setNakit([]);
      return;
    }
    try {
      setNakitLoading(true);
      const dateParams = getDateParams(filterOverride, filterTypeOverride, dateRangeOverride, selectedDateOverride);
      const response = await ReportService.payment.getBranchPaymentReport({
        token,
        CompanyCode: companyCode,
        BranchCode: resolvedBranchCode || undefined,
        PaymentType: 'Nakit',
        ...dateParams
      });

      if (response.status === 200) {
        setNakit(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching nakit:', error);
    } finally {
      setNakitLoading(false);
    }
  };

  const fetchKrediKarti = async (filterOverride = null, filterTypeOverride = null, dateRangeOverride = null, selectedDateOverride = null, branchCodeOverride = null, adminCompanyOverride = undefined) => {
    const resolvedBranchCode = branchCodeOverride !== null && branchCodeOverride !== undefined ? branchCodeOverride : (selectedBranch || null);
    const companyCode = getCompanyCodeForRequest(resolvedBranchCode, adminCompanyOverride);
    if (!companyCode) {
      setKrediKarti([]);
      return;
    }
    try {
      setKrediKartiLoading(true);
      const dateParams = getDateParams(filterOverride, filterTypeOverride, dateRangeOverride, selectedDateOverride);
      const response = await ReportService.payment.getBranchPaymentReport({
        token,
        CompanyCode: companyCode,
        BranchCode: resolvedBranchCode || undefined,
        PaymentType: 'Kredi Kartı',
        ...dateParams
      });

      if (response.status === 200) {
        setKrediKarti(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching kredi kartı:', error);
    } finally {
      setKrediKartiLoading(false);
    }
  };

  const fetchYemekKarti = async (filterOverride = null, filterTypeOverride = null, dateRangeOverride = null, selectedDateOverride = null, branchCodeOverride = null, adminCompanyOverride = undefined) => {
    const resolvedBranchCode = branchCodeOverride !== null && branchCodeOverride !== undefined ? branchCodeOverride : (selectedBranch || null);
    const companyCode = getCompanyCodeForRequest(resolvedBranchCode, adminCompanyOverride);
    if (!companyCode) {
      setYemekKarti([]);
      return;
    }
    try {
      setYemekKartiLoading(true);
      const dateParams = getDateParams(filterOverride, filterTypeOverride, dateRangeOverride, selectedDateOverride);
      const response = await ReportService.payment.getBranchPaymentReport({
        token,
        CompanyCode: companyCode,
        BranchCode: resolvedBranchCode || undefined,
        PaymentType: 'Yemek Kartı',
        ...dateParams
      });

      if (response.status === 200) {
        setYemekKarti(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching yemek kartı:', error);
    } finally {
      setYemekKartiLoading(false);
    }
  };

  const fetchAllReports = async (filterOverride = null, filterTypeOverride = null, dateRangeOverride = null, selectedDateOverride = null, branchCodeOverride = null, adminCompanyOverride = undefined) => {
    setLoading(true);
    await Promise.all([
      fetchToplamCiro(filterOverride, filterTypeOverride, dateRangeOverride, selectedDateOverride, branchCodeOverride, adminCompanyOverride),
      fetchNakit(filterOverride, filterTypeOverride, dateRangeOverride, selectedDateOverride, branchCodeOverride, adminCompanyOverride),
      fetchKrediKarti(filterOverride, filterTypeOverride, dateRangeOverride, selectedDateOverride, branchCodeOverride, adminCompanyOverride),
      fetchYemekKarti(filterOverride, filterTypeOverride, dateRangeOverride, selectedDateOverride, branchCodeOverride, adminCompanyOverride)
    ]);
    setLoading(false);
  };

  const handleBranchChange = (value) => {
    setSelectedBranch(value);
    fetchAllReports(dateFilter, dateFilterType, dateRange, selectedDate, value);
  };

  const openFilters = () => {
    setDraftDateFilterType(dateFilterType);
    setDraftDateFilter(dateFilter);
    setDraftSelectedDate(selectedDate);
    setDraftDateRange(dateRange);
    setDraftSelectedBranch(selectedBranch);
    setDraftAdminCompanyCode(adminCompanyCode);
    setIsFilterOpen(true);
  };

  const applyFilters = () => {
    setDateFilterType(draftDateFilterType);
    setDateFilter(draftDateFilter);
    setSelectedDate(draftSelectedDate);
    setDateRange(draftDateRange);
    setSelectedBranch(draftSelectedBranch);
    if (needsCompanyPicker) {
      setAdminCompanyCode(draftAdminCompanyCode);
    }
    setIsFilterOpen(false);

    if (draftDateFilterType === 'range') {
      fetchAllReports(null, 'range', draftDateRange, null, draftSelectedBranch, draftAdminCompanyCode);
    } else if (draftDateFilter === 'custom') {
      fetchAllReports('custom', 'quick', null, draftSelectedDate, draftSelectedBranch, draftAdminCompanyCode);
    } else {
      fetchAllReports(draftDateFilter, 'quick', null, null, draftSelectedBranch, draftAdminCompanyCode);
    }
  };

  const clearFilters = () => {
    const defaultDateFilterType = 'quick';
    const defaultDateFilter = 'today';
    const defaultSelectedDate = dayjs();
    const defaultDateRange = [dayjs().subtract(7, 'day'), dayjs()];
    const defaultSelectedBranch = null;

    setDraftDateFilterType(defaultDateFilterType);
    setDraftDateFilter(defaultDateFilter);
    setDraftSelectedDate(defaultSelectedDate);
    setDraftDateRange(defaultDateRange);
    setDraftSelectedBranch(defaultSelectedBranch);

    setDateFilterType(defaultDateFilterType);
    setDateFilter(defaultDateFilter);
    setSelectedDate(defaultSelectedDate);
    setDateRange(defaultDateRange);
    setSelectedBranch(defaultSelectedBranch);

    fetchAllReports(defaultDateFilter, defaultDateFilterType, null, null, null);
  };

  const handleMobileDraftRangeDateChange = (index, date) => {
    const nextRange = getUpdatedMobileDateRange(draftDateRange, index, date, 7);
    if (!nextRange) return;
    setDraftDateRange(nextRange);
  };

  useEffect(() => {
    if (branchesLoading) {
      return;
    }
    if (!effectiveCompanyCode) {
      setLoading(false);
      return;
    }
    fetchAllReports();
  }, [effectiveCompanyCode, branchesLoading]);

  const isToday = () => {
    if (dateFilterType === 'range') {
      return false;
    }
    if (dateFilter === 'custom') {
      const selected = selectedDate;
      const today = dayjs();
      return selected.format('YYYY-MM-DD') === today.format('YYYY-MM-DD');
    }
    return dateFilter === 'today';
  };

  const getReportTitle = (baseTitle) => {
    if (isToday()) {
      return `Anlık ${baseTitle}`;
    }
    if (baseTitle.includes('Toplam')) {
      return baseTitle;
    }
    return `Toplam ${baseTitle}`;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const calculateTotal = (data) => {
    return data.reduce((sum, row) => sum + parseFloat(row.ToplamCiro || row.ToplamTutar || 0), 0);
  };

  const tableColumns = [
    {
      title: <span className="text-base">Şube Adı</span>,
      dataIndex: 'SubeAdi',
      key: 'SubeAdi',
      width: isMobile ? '40%' : '50%',
      render: (text) => <span className="text-base">{text}</span>,
      ellipsis: isMobile
    },
    {
      title: <span className="text-base">Tutar</span>,
      dataIndex: 'ToplamCiro',
      key: 'ToplamCiro',
      width: isMobile ? '60%' : '50%',
      render: (value, record) => (
        <span className="text-base font-semibold">
          {formatCurrency(value || record.ToplamTutar || 0)}
        </span>
      ),
      sorter: (a, b) => (a.ToplamCiro || a.ToplamTutar || 0) - (b.ToplamCiro || b.ToplamTutar || 0),
    },
  ];

  const filterData = (data) => {
    if (!searchText) return data;
    const needle = searchText.toLowerCase();
    return data.filter(row =>
      String(row.SubeAdi || '').toLowerCase().includes(needle) ||
      String(row.SubeKodu || '').toLowerCase().includes(needle)
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-50 via-white to-slate-50 border border-slate-200/60 shadow-lg backdrop-blur-sm mb-4">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-indigo-500/5"></div>
        <div className="relative px-3 py-2 sm:px-4 sm:py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg sm:rounded-xl shadow-lg">
              <DollarOutlined className="text-white text-lg sm:text-xl" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent leading-tight">
                Kasa Raporları
              </h1>
              <p className="text-slate-500 text-xs sm:text-sm font-medium">
                Şube bazlı kasa raporları
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchAllReports(dateFilter, dateFilterType, dateRange, selectedDate, selectedBranch)}
              loading={toplamCiroLoading || nakitLoading || krediKartiLoading || yemekKartiLoading}
            >
              Yenile
            </Button>
            <Button icon={<FilterOutlined />} onClick={openFilters}>Filtreler</Button>
          </div>
        </div>
      </div>


      {/* Toplam Ciro Raporu */}
      <Card
        title={<h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold`}>{getReportTitle('Ciro')}</h2>}
        bordered={false}
        className="shadow-lg rounded-2xl border border-gray-100 bg-white mb-4"
        bodyStyle={{ padding: isMobile ? 12 : 18 }}
      >
        <div className="mb-3">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Şube adı/kodu ara"
            className="w-full sm:w-64"
          />
        </div>
        <Table
          columns={tableColumns}
          dataSource={filterData(toplamCiro)}
          loading={toplamCiroLoading}
          rowKey="SubeKodu"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Toplam ${total} şube`,
          }}
          size={isMobile ? 'small' : 'middle'}
          summary={(pageData) => {
            const total = calculateTotal(pageData);
            return (
              <Table.Summary fixed>
                <Table.Summary.Row className="bg-gray-50">
                  <Table.Summary.Cell index={0}>
                    <span className="font-bold text-base">Genel Toplam</span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    <span className="font-bold text-base text-red-600">{formatCurrency(total)}</span>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </Card>

      {/* Nakit Raporu */}
      <Card
        title={<h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold`}>{getReportTitle('Nakit')}</h2>}
        bordered={false}
        className="shadow-lg rounded-2xl border border-gray-100 bg-white mb-4"
        bodyStyle={{ padding: isMobile ? 12 : 18 }}
      >
        <Table
          columns={tableColumns}
          dataSource={filterData(nakit)}
          loading={nakitLoading}
          rowKey="SubeKodu"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Toplam ${total} şube`,
          }}
          size={isMobile ? 'small' : 'middle'}
          summary={(pageData) => {
            const total = calculateTotal(pageData);
            return (
              <Table.Summary fixed>
                <Table.Summary.Row className="bg-gray-50">
                  <Table.Summary.Cell index={0}>
                    <span className="font-bold text-base">Genel Toplam</span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    <span className="font-bold text-base text-green-600">{formatCurrency(total)}</span>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </Card>

      {/* Kredi Kartı Raporu */}
      <Card
        title={<h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold`}>{getReportTitle('Kredi Kartı')}</h2>}
        bordered={false}
        className="shadow-lg rounded-2xl border border-gray-100 bg-white mb-4"
        bodyStyle={{ padding: isMobile ? 12 : 18 }}
      >
        <Table
          columns={tableColumns}
          dataSource={filterData(krediKarti)}
          loading={krediKartiLoading}
          rowKey="SubeKodu"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Toplam ${total} şube`,
          }}
          size={isMobile ? 'small' : 'middle'}
          summary={(pageData) => {
            const total = calculateTotal(pageData);
            return (
              <Table.Summary fixed>
                <Table.Summary.Row className="bg-gray-50">
                  <Table.Summary.Cell index={0}>
                    <span className="font-bold text-base">Genel Toplam</span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    <span className="font-bold text-base text-blue-600">{formatCurrency(total)}</span>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </Card>

      {/* Yemek Kartı Raporu */}
      <Card
        title={<h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold`}>{getReportTitle('Yemek Kartı')}</h2>}
        bordered={false}
        className="shadow-lg rounded-2xl border border-gray-100 bg-white mb-4"
        bodyStyle={{ padding: isMobile ? 12 : 18 }}
      >
        <Table
          columns={tableColumns}
          dataSource={filterData(yemekKarti)}
          loading={yemekKartiLoading}
          rowKey="SubeKodu"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Toplam ${total} şube`,
          }}
          size={isMobile ? 'small' : 'middle'}
          summary={(pageData) => {
            const total = calculateTotal(pageData);
            return (
              <Table.Summary fixed>
                <Table.Summary.Row className="bg-gray-50">
                  <Table.Summary.Cell index={0}>
                    <span className="font-bold text-base">Genel Toplam</span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    <span className="font-bold text-base text-blue-600">{formatCurrency(total)}</span>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </Card>

      <Drawer title="Filtreler" placement="right" open={isFilterOpen} onClose={() => setIsFilterOpen(false)} width={isMobile ? 280 : 360}>
        <Space direction="vertical" size="middle" className="w-full">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">Filtre Tipi</span>
            <Radio.Group
              value={draftDateFilterType}
              onChange={(e) => setDraftDateFilterType(e.target.value)}
              className="w-full"
            >
              <Radio.Button value="quick" className="w-1/2 text-center">Gün</Radio.Button>
              <Radio.Button value="range" className="w-1/2 text-center">Tarih Aralığı</Radio.Button>
            </Radio.Group>
          </div>

          {draftDateFilterType === 'quick' ? (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-slate-500">Gün Seçimi</span>
                <div className="flex flex-col gap-2">
                  {[
                    { label: 'Bugün', value: 'today' },
                    { label: 'Dün', value: 'yesterday' },
                    { label: 'Gün Seçin', value: 'custom' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setDraftDateFilter(option.value)}
                      className={`
                        px-3 py-2 text-sm font-semibold transition-all duration-300 ease-out rounded-lg
                        ${draftDateFilter === option.value
                          ? 'bg-gradient-to-r from-rose-500 to-red-600 text-white shadow-lg'
                          : 'text-slate-600 hover:text-slate-800 hover:bg-gray-100 border border-gray-200'
                        }
                      `}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              {draftDateFilter === 'custom' && (
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-slate-500">Özel Gün</span>
                  <DatePicker
                    value={draftSelectedDate}
                    onChange={(date) => setDraftSelectedDate(date)}
                    format="DD.MM.YYYY"
                    className="w-full"
                    placeholder="Gün seçin"
                    placement="bottomLeft"
                    getPopupContainer={(trigger) => trigger?.parentElement || document.body}
                    popupClassName="mobile-drawer-date-popup"
                  />
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col gap-1">
              <span className="text-xs text-slate-500">Tarih Aralığı</span>
              {isMobile ? (
                <div className="flex items-center gap-1 w-full">
                  <DatePicker
                    value={draftDateRange?.[0]}
                    onChange={(date) => handleMobileDraftRangeDateChange(0, date)}
                    format="DD.MM.YYYY"
                    placeholder="Başlangıç"
                    className="w-full"
                    size="small"
                    allowClear={false}
                    placement="bottomLeft"
                    getPopupContainer={(trigger) => trigger?.parentElement || document.body}
                    popupClassName="mobile-drawer-date-popup"
                  />
                  <span className="text-slate-400 text-xs">-</span>
                  <DatePicker
                    value={draftDateRange?.[1]}
                    onChange={(date) => handleMobileDraftRangeDateChange(1, date)}
                    format="DD.MM.YYYY"
                    placeholder="Bitiş"
                    className="w-full"
                    size="small"
                    allowClear={false}
                    placement="bottomLeft"
                    getPopupContainer={(trigger) => trigger?.parentElement || document.body}
                    popupClassName="mobile-drawer-date-popup"
                  />
                </div>
              ) : (
                <RangePicker
                  value={draftDateRange}
                  onChange={(dates) => setDraftDateRange(dates || [])}
                  format="DD.MM.YYYY"
                  className="w-full"
                  placement="bottomLeft"
                  getPopupContainer={(trigger) => trigger?.parentElement || document.body}
                />
              )}
            </div>
          )}

          <ReportCompanySelect
            companies={companies}
            value={draftAdminCompanyCode}
            onChange={(value) => {
              setDraftAdminCompanyCode(value);
              setDraftSelectedBranch(null);
            }}
            loading={branchesLoading}
          />

          <ReportBranchSelect
            allBranches={allBranches}
            needsCompanyPicker={needsCompanyPicker}
            draftAdminCompanyCode={draftAdminCompanyCode}
            adminCompanyCode={adminCompanyCode}
            effectiveCompanyCode={effectiveCompanyCode}
            value={draftSelectedBranch}
            onChange={setDraftSelectedBranch}
            loading={branchesLoading}
          />

          <div className="flex items-center justify-between gap-2 pt-2">
            <Button onClick={clearFilters}>Temizle</Button>
            <Button type="primary" danger ghost={false} onClick={applyFilters} className="!bg-rose-600 !border-rose-600 !text-white hover:!bg-rose-700 hover:!border-rose-700">Uygula</Button>
          </div>
        </Space>
      </Drawer>
    </div>
  );
};

export default PaymentReports;

